import React, { FC, useMemo } from 'react';
import styles from './TransferPortfolioModal.module.scss';

import { ModalWithAside, ShouldRender } from 'src/components';
import InfoTextBlock from 'src/atoms/InfoTextBlock';
import WorkspaceDropdown from 'src/pages/ChooseWorkspacePage/components/WorkspaceDropdown';
import ErrorMessage from 'src/components/workspaces-components/ActionModal/components/ErrorMessage';
import ControlBtns from 'src/components/workspaces-components/ActionModal/components/ControlBtns';
import { getUserWorkspacesHook } from 'src/hooks/getUserWorkspacesHook';
import { TDropdownOption } from 'src/pages/ChooseWorkspacePage/model';
import { selectedWorkspacesSelector } from 'src/redux/selectors';
import { useSelector } from 'react-redux';
import { ACCESS_MODE } from 'src/interfaces/workspacesReduxShapes';

type TTransferPortfolioModalProps = {
  portfolioName: string;
  workspaceName: string;
  isOpen: boolean;
  onToggleModal: () => void;
  onConfirm: () => void;
  onClose: () => void;
  onErrorClick: () => void;
  onChangeNewWorkspace: (value: { label: string; value: string } | null) => void;
  errorMsg: string | null;
  selectedNewWorkspace: { label: string; value: string } | null;
};

const TransferPortfolioModal: FC<TTransferPortfolioModalProps> = ({
  portfolioName,
  workspaceName,
  isOpen,
  onToggleModal,
  onClose,
  onConfirm,
  errorMsg,
  onChangeNewWorkspace,
  selectedNewWorkspace,
  onErrorClick
}) => {
  const { userWorkspaces, isLoading } = getUserWorkspacesHook();
  const selectedWorkspace = useSelector(selectedWorkspacesSelector);

  const options: TDropdownOption[] = useMemo(
    () =>
      userWorkspaces
        ?.filter(
          (item) => item.workspace.id !== selectedWorkspace?.workspace.id && item.access_mode !== ACCESS_MODE.VIEWER
        )
        ?.map((item) => ({
          value: item.workspace.id,
          label: item.workspace.name
        })),
    [selectedWorkspace, userWorkspaces]
  );

  return (
    <ModalWithAside isOpen={isOpen} onToggle={onToggleModal}>
      <ShouldRender should={isOpen}>
        <div className={styles.container}>
          <h2 className={styles.title}>Transfer portfolio</h2>

          <div className={styles.divider} />

          {errorMsg ? (
            <ErrorMessage onClick={onErrorClick} message={errorMsg} />
          ) : (
            <>
              <InfoTextBlock classes={{ wrapper: styles.descriptionWrapper }}>
                {`You are about to transfer ${portfolioName} from ${workspaceName} to another workspace.`} <br />
                <br />
                Please choose a target workspace:
              </InfoTextBlock>

              <WorkspaceDropdown
                selectedWorkspace={selectedNewWorkspace}
                options={options}
                onChangeValue={onChangeNewWorkspace}
                classes={{ root: styles.dropdown }}
                width="100%"
                isDisabled={isLoading}
              />

              <ControlBtns onClose={onClose} onConfirm={onConfirm} isDisabledConfirm={!selectedNewWorkspace} />
            </>
          )}
        </div>
      </ShouldRender>
    </ModalWithAside>
  );
};

export default TransferPortfolioModal;
